/**
 * The respondent flow: instructions, landing, 17 question screens, completion.
 *
 * The instructions gate is shown only to a respondent who is starting fresh. A
 * resume goes straight back to the question they left, because they have
 * already read it and a live session should not make them read it twice.
 *
 * Resume rules, spec FR5:
 *   - localStorage holds the response id and the first name. Nothing else.
 *   - A stored id is verified against the server before it is used. If the
 *     server says complete, or does not know it, the keys are discarded and the
 *     respondent gets a clean form. Never resume into someone else's response.
 *   - Progress is rebuilt from the server's answered_positions. Where the client
 *     and the server disagree, the server wins silently.
 *   - The keys are cleared the moment a response completes, because a laptop at
 *     a live session may be handed to the next person.
 *
 * Navigation: one step back, spent once they move forward again. See
 * navigation.ts. A resumed session cannot step back into an earlier session's
 * answers, because those selections are never stored client side.
 *
 * Failures never surface as raw text. Anything that stops the flow, a lost
 * connection, a 429, a 5xx, a response that turns out to be complete, becomes a
 * designed state screen with a single retry that re-runs exactly the action
 * that failed. The flow underneath stays mounted, so a half-filled form or a
 * half-made choice is still there when the retry succeeds.
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { QUESTIONS, TOTAL_QUESTIONS, getQuestion, type Slot } from '@dna17q/shared';
import { ApiRequestError, NetworkError, completeResponse, getProgress, saveAnswer } from './api.ts';
import { clearSession, readSession, saveSession } from './storage.ts';
import { EMPTY_SELECTION, type Selection } from './selection.ts';
import { canGoBack, goBack, goForward, startNavigation, type Navigation } from './navigation.ts';
import { Backdrop } from './components/Backdrop.tsx';
import { Header } from './components/Header.tsx';
import { Instructions } from './screens/Instructions.tsx';
import { Landing } from './screens/Landing.tsx';
import { Question } from './screens/Question.tsx';
import { Complete } from './screens/Complete.tsx';
import {
  AlreadyComplete,
  GenericError,
  Loading,
  NotFound,
  RateLimited,
  Unreachable,
} from './screens/StateScreens.tsx';
import { useEffectOnce } from './useEffectOnce.ts';

type Phase = 'checking' | 'instructions' | 'landing' | 'questions' | 'complete';

/** Something that has stopped the flow. Shown instead of it until resolved. */
type Blocker =
  | { kind: 'unreachable'; retry: () => void }
  | { kind: 'rate_limited'; retryAfter: number; retry: () => void }
  | { kind: 'already_complete' }
  | { kind: 'error'; detail: string | null; retry: (() => void) | null };

/** Spec FR9. Read once at load, before any navigation can drop the query string. */
function readSource(): string | null {
  try {
    return new URL(window.location.href).searchParams.get('src');
  } catch {
    return null;
  }
}

/**
 * The app serves one address. Pages falls back to index.html for any other
 * path, so the 404 has to be rendered here rather than by the host.
 */
function isKnownPath(): boolean {
  const path = window.location.pathname;
  return path === '/' || path === '/index.html';
}

function firstMissing(positions: number[]): number | null {
  const present = new Set(positions);
  for (let qs = 1; qs <= TOTAL_QUESTIONS; qs++) {
    if (!present.has(qs)) return qs;
  }
  return null;
}

const ALL_POSITIONS = Array.from({ length: TOTAL_QUESTIONS }, (_, i) => i + 1);

export function App() {
  const source = useMemo(readSource, []);
  const knownPath = useMemo(isKnownPath, []);

  // A fresh visitor goes straight to the instructions. Only a stored id needs
  // the server checked first, so only then does the skeleton ever paint. This
  // matters: painting the skeleton and then swapping it for a taller card was
  // a visible flash and a layout shift on every first load.
  const [phase, setPhase] = useState<Phase>(() => (readSession().responseId ? 'checking' : 'instructions'));
  const [blocker, setBlocker] = useState<Blocker | null>(null);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [nav, setNav] = useState<Navigation>(() => startNavigation(0));
  const [answered, setAnswered] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  /** In-memory only, deliberately. Never written to storage. */
  const selections = useRef<Map<number, Selection>>(new Map());

  /** Maps a failure onto its state screen. `retry` re-runs the action that failed. */
  const raise = useCallback((err: unknown, retry: (() => void) | null) => {
    const dismiss = () => setBlocker(null);
    if (err instanceof NetworkError) {
      setBlocker({ kind: 'unreachable', retry: retry ?? dismiss });
      return;
    }
    if (err instanceof ApiRequestError) {
      if (err.status === 429) {
        setBlocker({ kind: 'rate_limited', retryAfter: err.body.retry_after ?? 60, retry: retry ?? dismiss });
        return;
      }
      if (err.body.error === 'response_complete') {
        // Spec FR7: no retakes. The device must be clean for the next person.
        clearSession();
        setBlocker({ kind: 'already_complete' });
        return;
      }
      setBlocker({
        kind: 'error',
        detail: err.body.error === 'internal_error' ? null : err.body.message,
        retry,
      });
      return;
    }
    setBlocker({ kind: 'error', detail: null, retry });
  }, []);

  const verifyStoredSession = useCallback(async () => {
    const stored = readSession();
    if (!stored.responseId) {
      setPhase('instructions');
      return;
    }

    setBlocker(null);
    setPhase('checking');
    try {
      const progress = await getProgress(stored.responseId);

      // Spec FR5 condition 1. A finished response must never be resumed into.
      if (progress.status === 'complete') {
        clearSession();
        setPhase('instructions');
        return;
      }

      // Spec FR5 condition 2. The server decides how far this response got.
      const positions = progress.answered_positions;
      const next = progress.next_position ?? firstMissing(positions);

      if (next === null) {
        // All 17 answered but the final request never landed, so finish it now.
        // The server re-checks that all 17 are present before it agrees.
        await completeResponse(stored.responseId);
        clearSession();
        setFirstName(stored.firstName ?? '');
        setPhase('complete');
        return;
      }

      setResponseId(stored.responseId);
      setFirstName(stored.firstName ?? '');
      setAnswered(new Set(positions));
      selections.current = new Map();
      setNotice(null);
      setNav(startNavigation(next - 1));
      setPhase('questions');
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 404) {
        // The server does not know this id. Discard and start fresh.
        clearSession();
        setPhase('instructions');
        return;
      }
      if (err instanceof ApiRequestError && err.body.error === 'incomplete_response') {
        // Completing on resume found a gap after all. Take the server's view.
        const missing = err.body.positions ?? [];
        setResponseId(stored.responseId);
        setFirstName(stored.firstName ?? '');
        setAnswered(new Set(ALL_POSITIONS.filter((p) => !missing.includes(p))));
        selections.current = new Map();
        setNav(startNavigation((missing[0] ?? 1) - 1));
        setPhase('questions');
        return;
      }
      // Unreachable, rate limited, or a server fault. Neither resume nor
      // discard: the stored id is the only handle on any saved answers.
      raise(err, () => void verifyStoredSession());
    }
  }, [raise]);

  useEffectOnce(() => {
    if (knownPath && readSession().responseId) void verifyStoredSession();
  });

  function onStarted(newResponseId: string, newFirstName: string) {
    saveSession(newResponseId, newFirstName);
    selections.current = new Map();
    setResponseId(newResponseId);
    setFirstName(newFirstName);
    setAnswered(new Set());
    setNav(startNavigation(0));
    setNotice(null);
    setBlocker(null);
    setPhase('questions');
  }

  function onNext(selection: { most: Slot; least: Slot }, timeSpentMs: number) {
    if (!responseId || busy) return;
    const current = nav;
    const question = getQuestion(QUESTIONS[current.index]!.qsPosition);

    const attempt = async () => {
      setBusy(true);
      setBlocker(null);
      setNotice(null);
      try {
        // Spec FR5: written as it is made, so a closed tab cannot lose it. The
        // words are sent verbatim from the frozen constants.
        await saveAnswer(responseId, question.qsPosition, {
          most_word: question.words[selection.most - 1]!,
          least_word: question.words[selection.least - 1]!,
          time_spent_ms: timeSpentMs,
        });

        selections.current.set(question.qsPosition, selection);
        setAnswered((prev) => new Set(prev).add(question.qsPosition));

        if (current.index + 1 < TOTAL_QUESTIONS) {
          setNav(goForward(current, TOTAL_QUESTIONS));
          setBusy(false);
          return;
        }

        // Last question. The server re-checks that all 17 are present. Spec FR4.
        await completeResponse(responseId);
        clearSession();
        setPhase('complete');
        setBusy(false);
      } catch (err) {
        setBusy(false);
        if (err instanceof ApiRequestError && err.body.error === 'incomplete_response') {
          // The server disagrees with our view of progress, so take its answer.
          const missing = err.body.positions ?? [];
          setAnswered(new Set(ALL_POSITIONS.filter((p) => !missing.includes(p))));
          if (missing[0]) setNav(startNavigation(missing[0] - 1));
          setNotice('Some questions still need an answer. We have taken you back to the first one.');
          return;
        }
        raise(err, () => void attempt());
      }
    };

    void attempt();
  }

  function onBack() {
    setNotice(null);
    setNav(goBack(nav));
  }

  if (!knownPath) {
    return (
      <Shell>
        <NotFound />
      </Shell>
    );
  }

  let flow: ReactNode = null;
  switch (phase) {
    case 'checking':
      flow = <Loading />;
      break;
    case 'instructions':
      flow = <Instructions onAcknowledged={() => setPhase('landing')} />;
      break;
    case 'landing':
      flow = <Landing source={source} onStarted={onStarted} onFailure={raise} />;
      break;
    case 'complete':
      flow = <Complete firstName={firstName} />;
      break;
    case 'questions': {
      const question = QUESTIONS[nav.index]!;
      flow = responseId ? (
        <Question
          key={question.qsPosition}
          question={question}
          initial={selections.current.get(question.qsPosition) ?? EMPTY_SELECTION}
          answeredCount={answered.size}
          isLast={nav.index + 1 === TOTAL_QUESTIONS}
          canGoBack={canGoBack(nav)}
          busy={busy}
          notice={notice}
          onBack={onBack}
          onNext={onNext}
          onChange={(selection) => selections.current.set(question.qsPosition, selection)}
        />
      ) : null;
      break;
    }
  }

  return (
    <Shell>
      {blocker ? renderBlocker(blocker) : null}
      {/* Kept mounted while a state screen shows, so form values and a
          half-made choice survive the retry. */}
      <div hidden={blocker !== null}>{flow}</div>
    </Shell>
  );
}

function renderBlocker(blocker: Blocker): ReactNode {
  switch (blocker.kind) {
    case 'unreachable':
      return <Unreachable onRetry={blocker.retry} />;
    case 'rate_limited':
      return <RateLimited retryAfter={blocker.retryAfter} onRetry={blocker.retry} />;
    case 'already_complete':
      return <AlreadyComplete />;
    case 'error':
      return <GenericError detail={blocker.detail} onRetry={blocker.retry} />;
  }
}

/** Backdrop behind, header on top, one centred column. Every screen renders inside it. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <>
      <Backdrop />
      <Header />
      <main className="page">
        <div className="stack">{children}</div>
      </main>
    </>
  );
}
